import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { AuthShell } from "./ForgotPassword";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`).then(r => setInfo(r.data)).catch(e => setError(formatApiError(e.response?.data?.detail) || "This link is not valid."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not reset password"); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell>
      <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400 mb-2">Account help</div>
      <h2 className="text-2xl font-bold tracking-tight mb-4">Set a new password</h2>
      {!info && !error && <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="w-4 h-4 animate-spin" /> Checking your link…</div>}
      {error && (
        <div className="space-y-4" data-testid="reset-invalid">
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>
          <Button asChild className="w-full bg-orange-500 hover:bg-orange-600 text-white" data-testid="reset-request-new"><Link to="/forgot-password">Request a new link</Link></Button>
        </div>
      )}
      {info && (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <p className="text-sm text-neutral-600">For <span className="font-medium">{info.email}</span>. Use at least 8 characters.</p>
          <div className="space-y-1.5">
            <Label htmlFor="rp-1" className="text-xs uppercase font-mono tracking-wider text-neutral-600">New password</Label>
            <div className="relative">
              <Input id="rp-1" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-password" className="bg-[#FFFFFF] border-[#E4E7EC] pr-10" />
              <button type="button" onClick={() => setShow(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500" aria-label="Show password" data-testid="reset-toggle-show">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rp-2" className="text-xs uppercase font-mono tracking-wider text-neutral-600">Confirm new password</Label>
            <Input id="rp-2" type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-password-confirm" className="bg-[#FFFFFF] border-[#E4E7EC]" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600 text-white" data-testid="reset-submit">
            {busy ? "Saving..." : "Update password"}
          </Button>
        </form>
      )}
      <Link to="/login" className="mt-6 inline-block text-xs text-neutral-600 hover:text-orange-600" data-testid="reset-back-to-login">Back to sign in</Link>
    </AuthShell>
  );
}
